import type { SxProps } from '@mui/material';
import { asReadonlyBS, Closables, createSetterBS } from '@ocodelib/ex-util/rx';
import type { IframeController } from '@ocodelib/iframe-parent';
import { useObservable, useUnmount } from '@ocodelib/mui/hooks';
import clsx from 'clsx';
import type * as monaco from 'monaco-editor';
import { useEffect, useRef, useState } from 'react';
import { BehaviorSubject, fromEvent, map, Subject, takeUntil, type Observable } from 'rxjs';
import { IframeWrapperNoSsr } from './IframeWrapperNoSsr';

type IframeMonacoApp = ReturnType<typeof createApp>;

type MonacoEditorOptions = {
  language: string;
  line?: number;
  options?: monaco.editor.IEditorOptions & monaco.editor.IGlobalEditorOptions;
};

export type IframeMonacoEditorType = {
  iframeController: IframeController;
  setOptions: (options: MonacoEditorOptions) => Promise<void>;
  getText: () => Promise<string | null>;
  updateTheme: (theme: string) => Promise<void>;
  updateLang: (lang: string) => Promise<void>;
  setText: (text: string) => Promise<void>;
  text$: Observable<string | null>;
  close: () => void;
};

interface Props {
  sx?: SxProps;
  className?: string;
  datiBaseURL: string;
  revision?: string | number | bigint;
  onLoadEditor?: (editor: IframeMonacoEditorType | undefined) => void;
}

/**
 * @example
<IframeMonacoEditorWrapper
      onLoadEditor={(editor) => {
        if (!editor) return;
        editor.setOptions({
          language: 'javascript',
          options: {
            theme: 'dracula',
            automaticLayout: true,
            minimap: {
              enabled: false,
            },
          },
        });
        editor.setText(
          'console.log(`hello world`);\n for(let i=0;i<10;i++)\n\tconsole.log(1234);\n\n\n',
        );
      }}
    />

 */
export function IframeMonacoEditorWrapper(props: Props) {
  const { sx, className, datiBaseURL, revision } = props;
  const onLoadEditorRef = useRef(props.onLoadEditor);
  onLoadEditorRef.current = props.onLoadEditor;
  const [iframeController, setIframeController] = useState<IframeController | null>(null);
  const [monacoApp, setMonacoApp] = useState<IframeMonacoApp>();
  const editor = useObservable(monacoApp?.editor$);

  useUnmount(() => {
    monacoApp?.destroy();
    setMonacoApp(undefined);
  });

  useEffect(() => {
    onLoadEditorRef.current?.(editor ?? undefined);
  }, [editor]);

  const handleLoadIframe = (iframe: IframeController | null) => {
    setIframeController(iframe);
  };

  useEffect(() => {
    if (!monacoApp) return;
    monacoApp.setIframeController(iframeController);
    return () => {
      monacoApp.setIframeController(null);
    };
  }, [monacoApp, iframeController]);

  useEffect(() => {
    const app = createApp();
    app.init();
    setMonacoApp(app);
    return () => {
      app.destroy();
      setMonacoApp(undefined);
    };
  }, []);

  return (
    <IframeWrapperNoSsr
      key={revision}
      className={clsx('IframeMonacoEditorWrapper-root', className)}
      datiBaseURL={datiBaseURL}
      // devUrl="http://localhost:9016"
      urlPath="/sys.iframe/monaco/index.html"
      sx={sx}
      idPrefix="monaco" //
      onLoad={handleLoadIframe}
    />
  );
}

function createApp() {
  const iframeController$ = new BehaviorSubject<IframeController | null>(null);
  const closables = new Closables();

  function init() {
    console.log('IframeMonacoEditorWrapper.init()');
  }

  // iframe이 연결되면, 에디터 객체 생성
  const editor$ = iframeController$.pipe(
    map((iframeController) => {
      if (!iframeController) return null;
      return newEditor(iframeController);
    }),
  );

  /**
   * 신규 에디터 생성
   */
  const newEditor = (iframe: IframeController): IframeMonacoEditorType => {
    async function setText(text: string) {
      await iframe.runCmd('setEditorText', [text]);
    }

    async function getText(): Promise<string | null> {
      return (await iframe.runCmd('getEditorText')) as string | null;
    }

    async function setOptions(options: MonacoEditorOptions) {
      await iframe.runCmd('setEditorOptions', [options]);
    }

    async function updateTheme(theme: string) {
      await iframe.runCmd('updateEditorTheme', [theme]);
    }

    async function updateLang(lang: string) {
      await iframe.runCmd('updateEditorLanguage', [lang]);
    }

    // iframe에서 보내는 이벤트
    // type CustomEventMap = {
    //   'text-changed': (text: string | undefined) => void;
    // };

    const destroyTrigger$ = new Subject<any>();

    function close() {
      destroyTrigger$.next(Date.now());
      closables.remove(close);
    }

    closables.add(close);

    const text$ = fromEvent(iframe.events, 'text-changed').pipe(
      takeUntil(destroyTrigger$),
    ) as unknown as Observable<string | null>;

    return {
      iframeController: iframe,
      setOptions,
      getText,
      setText,
      updateTheme,
      updateLang,
      text$,
      close,
    };
  };

  function destroy() {
    console.log('IframeMonacoEditorWrapper.close()');
    closables.close();
  }

  return {
    init,
    destroy,
    iframeController$: asReadonlyBS(iframeController$),
    setIframeController: createSetterBS(iframeController$),
    editor$,
  };
}
